import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { requestService } from '../../services/requestService';

const RequestView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (id) {
      fetchRequest();
    }
  }, [id]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await requestService.getById(Number(id));
      setRequest(response.data);
    } catch (err: any) {
      console.error('Error loading request:', err);
      setError('Failed to load request details.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      case 'assigned':
      case 'active':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getStatusStep = (status: string) => {
    const steps: Record<string, number> = {
      pending: 0,
      assigned: 1,
      active: 2,
      invoice_creation: 3,
      invoice_sent: 3,
      payment_received: 4,
      completed: 5,
      cancelled: -1,
    };
    return steps[status] ?? 0;
  };

  const getImageUrl = (photo: any): string | null => {
    if (!photo) return null;
    if (typeof photo === 'string') return photo;
    let url = photo.url ?? photo.data?.attributes?.url ?? photo.attributes?.url ?? null;
    if (!url || typeof url !== 'string') return null;
    // Strapi returns relative paths (e.g. /uploads/...); prepend API base so the image loads from the backend
    if (url.startsWith('/')) {
      const API_URL = (process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '');
      url = `${API_URL}${url}`;
    }
    return url;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !request) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Request not found'}</Alert>
      </Container>
    );
  }

  const attrs = request.attributes || request;
  const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient;
  const serviceProvider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider;
  const beforePhoto = attrs.evidencePhotoBefore?.data || attrs.evidencePhotoBefore;
  const afterPhoto = attrs.evidencePhotoAfter?.data || attrs.evidencePhotoAfter;
  const beforeImageUrl = getImageUrl(beforePhoto);
  const afterImageUrl = getImageUrl(afterPhoto);

  return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <ArrowBackIcon 
            sx={{ cursor: 'pointer' }} 
            onClick={() => navigate(-1)}
          />
          <Typography variant="h4">
            Request #{attrs.requestNumber || `REQ-${request.id}`}
          </Typography>
          <Chip
            label={getStatusLabel(attrs.status || 'pending')}
            color={getStatusColor(attrs.status || 'pending')}
            size="medium"
          />
        </Stack>

        <Stack spacing={3}>
          {/* Workflow Stepper */}
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
              Workflow Progress
            </Typography>
            <Stepper 
              activeStep={getStatusStep(attrs.status || 'pending')} 
              alternativeLabel
              sx={{ mb: 2 }}
            >
              <Step><StepLabel>Pending</StepLabel></Step>
              <Step><StepLabel>Assigned</StepLabel></Step>
              <Step><StepLabel>Active</StepLabel></Step>
              <Step><StepLabel>Invoice Sent</StepLabel></Step>
              <Step><StepLabel>Payment Received</StepLabel></Step>
              <Step><StepLabel>Completed</StepLabel></Step>
            </Stepper>
            <Alert 
              severity={
                attrs.status === 'completed' ? 'success' :
                attrs.status === 'cancelled' ? 'error' :
                attrs.status === 'pending' ? 'info' : 'success'
              } 
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">
                <strong>Current Status:</strong> {getStatusLabel(attrs.status || 'pending')}
              </Typography>
            </Alert>
          </Card>

          {/* Request Information */}
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
              Request Information
            </Typography>
            {attrs.requiresPayment && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  🔴 REQUIRES PAYMENT
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  This is an extra service requiring payment (policy expired or service limit reached). 
                  Call center must contact customer to inform about pricing before assignment.
                </Typography>
              </Alert>
            )}
            <Stack spacing={2.5}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip 
                      label={getStatusLabel(attrs.status || 'pending')} 
                      color={getStatusColor(attrs.status || 'pending')} 
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                    Created By
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {attrs.createdByAI ? '🤖 AI Generated' : '👤 Manual Entry'}
                  </Typography>
                </Box>
              </Box>
              {attrs.phoneNumber && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                    Request Phone Number
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    📱 {attrs.phoneNumber}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                    Phone number from which this request was created (may differ from client number)
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                  Description
                </Typography>
                <Card variant="outlined" sx={{ mt: 1, p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {attrs.description || 'No description provided'}
                  </Typography>
                </Card>
              </Box>
              {attrs.scheduledDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                    Scheduled Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    📅 {new Date(attrs.scheduledDate).toLocaleString()}
                  </Typography>
                </Box>
              )}
              {attrs.completedDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                    Completed Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    ✅ {new Date(attrs.completedDate).toLocaleString()}
                  </Typography>
                </Box>
              )}
              {attrs.notes && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                    Additional Notes
                  </Typography>
                  <Card variant="outlined" sx={{ mt: 1, p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {attrs.notes}
                    </Typography>
                  </Card>
                </Box>
              )}
              
              {/* Before/After Photos */}
              {(beforeImageUrl || afterImageUrl) && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem', mb: 1, display: 'block' }}>
                    Evidence Photos
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {beforeImageUrl && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Before
                        </Typography>
                        <img
                          src={beforeImageUrl}
                          alt="Before"
                          style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                      </Box>
                    )}
                    {afterImageUrl && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          After
                        </Typography>
                        <img
                          src={afterImageUrl}
                          alt="After"
                          style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Stack>
          </Card>

          {/* Customer/Insurance Client Details */}
          {insuranceClient && (
            <Card variant="outlined" sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">Client Information</Typography>
                <Chip label="Insurance Client" size="small" color="primary" variant="outlined" />
              </Box>
              <Stack spacing={2}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                      Customer Name
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                      {insuranceClient.customerName || insuranceClient.companyName || insuranceClient.contactPerson || 'N/A'}
                    </Typography>
                  </Box>
                  {insuranceClient.phoneNumber && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Phone Number
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {insuranceClient.phoneNumber}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {insuranceClient.email && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                      {insuranceClient.email}
                    </Typography>
                  </Box>
                )}
                {insuranceClient.vinNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                      VIN Number
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                      {insuranceClient.vinNumber}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Card>
          )}

          {/* Service Provider Details */}
          {serviceProvider && (
            <Card variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                Service Provider
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                    Name
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {serviceProvider.name || 'N/A'}
                  </Typography>
                </Box>
                {serviceProvider.phone && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                      Phone
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                      {serviceProvider.phone}
                    </Typography>
                  </Box>
                )}
                {serviceProvider.email && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                      {serviceProvider.email}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Card>
          )}
        </Stack>
      </Container>
  );
};

export default RequestView;

















